import { fetchRegionFeatured } from 'api/region/static';
import { IRegionFeaturedResort, IRegionShort, IRelatedForRegion } from 'api/region/types';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { HeaderNav, HeaderNavCurLink } from 'components/common/HeaderNav';
import { HeadMeta } from 'components/common/HeadMeta';
import { RegionHeaderNav } from 'components/region/RegionHeaderNav';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { Layout } from 'components/resort/Layout/Layout';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import { I18nKey } from 'i18n/types';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { IRegionBubblingProps, RegionLayoutContext } from 'page-components/region/_context';
import { defaultImage } from 'page-components/region/constants';
import React, { useState } from 'react';
import { useFakeQueryWithInitialValue } from 'util/useQueryWithInitialValue';
import { RegionFPCTracker } from 'util/facebookAnalytics/components/RegionFPCTracker';
import { TemplateProps } from 'page-components/pathTypes';

export interface IRegionLayout extends TemplateProps {
  pathInfo: IRegionShort,
  featured: IRegionFeaturedResort,
  related: IRelatedForRegion,
  adTKey: string,
  meta: {
    title: I18nKey['seo'],
    description: I18nKey['seo'],
    redirectTo: string | null,
  },
  navigation: {
    head: HeaderNavCurLink,
    tab: CurrentTab,
    link: CurrentLink,
  },
  hero: {
    title: I18nKey['common'],
  }
}

export const RegionLayout = ({
  children,
  pathInfo,
  featured: preFeatured,
  adTKey,
  meta,
  navigation,
  hero,
  related,
}: IRegionLayout & { children: React.ReactNode }) => {
  const { t: seoT } = useOTSTranslation('seo');
  const { t } = useOTSTranslation('common');
  const [, setBubbleProps] = useState<IRegionBubblingProps>({});

  const {
    current: featured,
  } = useFakeQueryWithInitialValue(['region-fetchRegionFeatured', pathInfo.uuid],
    async () => fetchRegionFeatured(pathInfo.uuid),
    preFeatured);

  return (
    <RegionLayoutContext.Provider value={setBubbleProps}>
      <AdUnitContext
        region={pathInfo}
        adType="region"
        adTKey={adTKey}
        meta={{
          Fs: undefined,
          Rs: undefined,
          Th: undefined,
          Tl: undefined,
          snow: undefined,
          Wc: undefined,
        }}
      >
        <RegionFPCTracker
          title={pathInfo.title}
          uuid={pathInfo.uuid}
        />
        <HeadMeta
          title={seoT(meta.title, { region: pathInfo.title })}
          description={seoT(meta.description, { region: pathInfo.title })}
          image={featured?.details?.image ?? defaultImage}
          redirectTo={meta.redirectTo}
        />
        <HeaderNav current={navigation.head} />
        <TopAd id={`region-${pathInfo.uuid}`} />
        <div className="container pt-4">
          <RegionHeaderNav
            photo={featured?.details?.image ?? defaultImage}
            resort={featured?.resort}
            link={navigation.link}
            region={pathInfo}
            tab={navigation.tab}
            related={related}
            title={t(hero.title, { region: pathInfo.title })}
          />
          <Layout className="layout-container pt-0 pt-lg-4">
            {meta.redirectTo ? (
              <div
                className="w-100"
                style={{
                  height: '30vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}
              >
                <h3>
                  You are being redirected to
                  {' '}
                  {meta.redirectTo}
                  {' '}
                  domain
                </h3>
              </div>
            ) : children}
          </Layout>
        </div>
        <BottomMountains />
        <Footer />
      </AdUnitContext>
    </RegionLayoutContext.Provider>
  );
};
